// LIGHT by default — CRYB's brand is the lavender GLASS world (not dark). The
// reference designs' LAYOUT is the target, not their dark palette. Screens read
// colors.* directly, so these light values set the whole app. Keep the glass
// effect; never sweep to dark. (colorTokens.dark still exists for media/call
// surfaces that genuinely need a near-black backdrop.)
export const colors = {
  bg: '#E8E1F2',
  primary: '#6C5CE7',
  primaryLight: '#A855F7',
  gradient: ['#6C5CE7', '#A855F7'],
  white: '#FFFFFF',
  black: '#000000',
  ink: '#1A1A2E',
  ink2: '#5A5A6E',
  muted: '#8E8E9E',
  faint: '#B4B0CC',
  card: '#FFFFFF',
  cardBorder: 'rgba(0,0,0,0.06)',
  success: '#2DB55D',
  error: '#FF4757',
  warning: '#F59E0B',
  info: '#3B82F6',
  btcOrange: '#F7931A',
  ethBlue: '#627EEA',
};

export const spacing = {
  xs: 4,
  sm: 8,
  md: 12,
  lg: 16,
  xl: 20,
  xxl: 24,
  xxxl: 32,
};

// Shared layout constants used by the Screen/Header/Section primitives.
// Centralised so vertical rhythm is consistent across every screen and there
// are no magic header heights / hand-tuned top insets scattered per-screen.
export const layout = {
  // Intrinsic content height of an in-screen Header (the row itself, BEFORE the
  // safe-area top inset is added). 44 matches the iOS nav-bar tap target.
  headerHeight: 44,
  // Horizontal screen gutter — the standard left/right page padding.
  gutter: spacing.lg,
  // Vertical gap between stacked Sections (the page's block rhythm).
  sectionGap: spacing.xl,
  // First-Section top gap (sits directly under the header). Kept tight so there
  // is no dead band between the header and the first block of content.
  sectionTopGap: spacing.md,
  // Extra bottom padding so scrollable content clears the FLOATING glass tab bar
  // (components/nav/FloatingTabBar). The bar is absolutely positioned and detached
  // from the bottom, so it reserves no layout space — every scroller must pad by
  // this much to keep its last row visible. Sized for the bar's worst case
  // (notched device): host lift (max safe-area, ~34) + floating gap (~20) + bar
  // body (~52). Read it via `screenBottomInset` (components/ui/Screen) or the
  // `useTabBarClearance()` hook in components/nav.
  bottomNavInset: 108,
} as const;

export const radius = {
  sm: 8,
  md: 12,
  lg: 16,
  xl: 20,
  full: 9999,
};

export const fonts = {
  regular: { fontWeight: '400' as const },
  medium: { fontWeight: '500' as const },
  semibold: { fontWeight: '600' as const },
  bold: { fontWeight: '700' as const },
  extrabold: { fontWeight: '800' as const },
};

export const glass = {
  card: {
    backgroundColor: 'rgba(255,255,255,0.74)',
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.7)',
  },
  cardDark: {
    backgroundColor: 'rgba(26,26,46,0.6)',
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.1)',
  },
} as const;

// =============================================================================
// DESIGN-SYSTEM TOKENS (P0-A) — additive. constants/theme.ts is the ONLY source
// for colors/spacing/radius/fonts/shadow/animation/typographyScale. Nothing below
// removes or renames existing exports; screens must read from here, not hardcode.
// =============================================================================

// ── Typography scale ─────────────────────────────────────────────────────────
// One ramp for every text block. Pair with fonts.* for weight, e.g.
//   <Text style={[typographyScale.h1, fonts.bold]}>…</Text>
export const typographyScale = {
  h1: { fontSize: 32, lineHeight: 38, letterSpacing: -0.5, fontWeight: '800' as const },
  h2: { fontSize: 24, lineHeight: 30, letterSpacing: -0.3, fontWeight: '700' as const },
  h3: { fontSize: 18, lineHeight: 24, letterSpacing: -0.2, fontWeight: '600' as const },
  body: { fontSize: 15, lineHeight: 22, letterSpacing: 0, fontWeight: '400' as const },
  caption: { fontSize: 12, lineHeight: 16, letterSpacing: 0.2, fontWeight: '500' as const },
} as const;

// ── Elevation / shadow tokens ────────────────────────────────────────────────
// RN supports one shadow per View. Tuned to brand purple so surfaces lift off the
// lavender bg (never a flat grey). glass/tokens.ts re-exports card|bar|sheet from
// here so there is a single elevation source.
export const shadowTokens = {
  card: {
    shadowColor: 'rgba(108,92,231,0.13)',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 1,
    shadowRadius: 12,
    elevation: 5,
  },
  bar: {
    shadowColor: 'rgba(108,92,231,0.12)',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 1,
    shadowRadius: 10,
    elevation: 3,
  },
  sheet: {
    shadowColor: 'rgba(26,26,46,0.28)',
    shadowOffset: { width: 0, height: -4 },
    shadowOpacity: 1,
    shadowRadius: 24,
    elevation: 12,
  },
} as const;

// ── Motion tokens ────────────────────────────────────────────────────────────
// One motion language. duration.* for timings; spring{damping,stiffness} feeds
// react-native-reanimated withSpring / withTiming.
export const animationTokens = {
  duration: { fast: 120, base: 220, slow: 420, splash: 900 },
  spring: {
    // gentle, premium settle — no overshoot bounce
    damping: 18,
    stiffness: 180,
    mass: 1,
  },
  springBouncy: {
    damping: 12,
    stiffness: 220,
    mass: 1,
  },
} as const;

// ── Semantic color tokens (light + dark) ─────────────────────────────────────
// Two modes only: 'light' (lavender, default) and 'dark' (voice/call). Consumers
// pick a mode via colorTokens[mode].*. All values derive from the existing brand
// palette above — no new hues introduced.
export const colorTokens = {
  light: {
    bg: colors.bg,
    surface: colors.white,
    text: colors.ink,
    textMuted: colors.ink2,
    textFaint: colors.muted,
    primary: colors.primary,
    primaryLight: colors.primaryLight,
    border: colors.cardBorder,
    success: colors.success,
    error: colors.error,
    warning: colors.warning,
    info: colors.info,
  },
  dark: {
    bg: '#0E0B1A',
    // Full-bleed media backdrop (reference screenshots are all DARK: IG live
    // player, DM context-menu, share sheet, cover editor). Near-black with a
    // faint violet cast so glass chrome + orbs still read over it. Additive:
    // consumed by MediaTopBar/MediaTransportBar and any full-bleed media surface.
    bgMedia: '#0A0A0F',
    surface: '#1A1A2E',
    text: colors.white,
    textMuted: 'rgba(255,255,255,0.72)',
    textFaint: 'rgba(255,255,255,0.45)',
    primary: colors.primaryLight,
    primaryLight: colors.primary,
    border: 'rgba(255,255,255,0.12)',
    success: colors.success,
    error: colors.error,
    warning: colors.warning,
    info: colors.info,
  },
} as const;

export type ThemeMode = keyof typeof colorTokens;

// ── Status color helper ──────────────────────────────────────────────────────
// Maps a 0–1 (or 0–100) progress fraction to a semantic status color for
// ProgressRow's tinted track + % pill. Low = warning (behind), mid = info
// (on track), high = success (done). Additive — reads only existing palette
// tokens, introduces no new hues.
//   statusColor(0.2) → warning · statusColor(0.6) → info · statusColor(0.95) → success
export function statusColor(pct: number): string {
  // Accept either 0–1 fractions or 0–100 percentages.
  const p = pct > 1 ? pct / 100 : pct;
  if (p >= 0.85) return colors.success;
  if (p >= 0.4) return colors.info;
  if (p >= 0.15) return colors.warning;
  return colors.error;
}

export const breakpoints = {
  phone: 0,
  tablet: 768,
  desktop: 1024,
};

export function getLayout(width: number) {
  if (width >= breakpoints.desktop) return 'desktop';
  if (width >= breakpoints.tablet) return 'tablet';
  return 'phone';
}
